import { useEffect } from 'react';
import ReactPlayer from 'react-player/youtube';
import { tl } from '../utils/helpers';

export default function TrailerModal({ item, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const hasTrailer = item.type === 'movie' && item.trailerUrl;

  const typeBadgeStyle = {
    padding: '0.22rem 0.6rem', borderRadius: 6,
    fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
    letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500,
    ...(item.type === 'movie'
      ? { background: 'rgba(255,77,122,.14)', border: '1px solid rgba(255,77,122,.35)', color: 'var(--rose)' }
      : item.type === 'book'
      ? { background: 'rgba(56,189,248,.12)', border: '1px solid rgba(56,189,248,.3)', color: 'var(--sky)' }
      : { background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.3)', color: 'var(--green)' }),
  };

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.92)',
        backdropFilter: 'blur(18px)', zIndex: 9000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem', animation: 'fadeIn 0.22s ease',
      }}
    >
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border2)',
        borderRadius: 24, overflow: 'hidden',
        width: '100%', maxWidth: 900,
        animation: 'modalIn 0.28s ease',
        maxHeight: '95vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface2)', flexShrink: 0 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 5 }}>
              <span style={typeBadgeStyle}>{tl(item.type)}</span>
              {hasTrailer && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: '0.61rem', color: 'var(--rose)' }}>
                  <span style={{ width: 6, height: 6, background: 'var(--rose)', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1.5s ease infinite' }} />
                  Official Trailer
                </span>
              )}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', fontWeight: 700 }}>{item.title}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.63rem', color: 'var(--muted)', marginTop: 3 }}>{item.year} · {item.by} · {item.runtime}</div>
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--surface3)', border: '1px solid var(--border)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>✕</button>
        </div>

        {/* Player */}
        <div style={{ overflowY: 'auto', flexShrink: 1 }}>
          {hasTrailer ? (
            <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
              <div style={{ position: 'absolute', inset: 0 }}>
                <ReactPlayer
                  url={item.trailerUrl}
                  width="100%" height="100%"
                  playing controls
                  config={{ youtube: { playerVars: { autoplay: 1, modestbranding: 1, rel: 0 } } }}
                />
              </div>
            </div>
          ) : (
            <div style={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', background: 'var(--bg1)', color: 'var(--muted)', fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
              <span style={{ fontSize: '2.5rem' }}>{item.type === 'book' ? '📖' : '🛍️'}</span>
              No trailer available for this {item.type}
            </div>
          )}

          {/* Info strip */}
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', padding: '1.25rem 1.4rem', borderTop: '1px solid var(--border)' }}>
            {[
              { l: 'Genre',    v: item.genre,                   c: 'var(--text)'  },
              { l: 'Rating',   v: item.rated,                   c: 'var(--text)'  },
              { l: 'Duration', v: item.runtime,                 c: 'var(--text)'  },
              { l: 'AI Match', v: `★ ${item.score}%`,           c: 'var(--teal)'  },
              { l: 'Awards',   v: item.awards ? `🏆 ${item.awards}` : null, c: 'var(--gold)' },
            ].filter(x => x.v).map(({ l, v, c }) => (
              <div key={l}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.59rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Description + Cast */}
          <div style={{ padding: '0 1.4rem 1.5rem', borderTop: '1px solid var(--border)' }}>
            <p style={{ fontSize: '0.84rem', color: 'var(--text2)', lineHeight: 1.72, marginTop: '1rem' }}>{item.longDesc}</p>
            {item.cast?.length > 0 && (
              <div style={{ marginTop: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Cast:</span>
                {item.cast.map(c => (
                  <span key={c} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.18rem 0.5rem', fontSize: '0.75rem', color: 'var(--text2)' }}>{c}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
